import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownToLine, ArrowUpFromLine, Scale, Shuffle, Undo2 } from 'lucide-react';
import {
  STOCK_MOVEMENT_ORIGIN_LABELS,
  STOCK_MOVEMENT_ORIGINS,
  STOCK_MOVEMENT_TYPE_LABELS,
  STOCK_MOVEMENT_TYPES,
  formatCents,
  formatQty,
  type Paginated,
  type StockMovement,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { blank, formatDateTime } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField } from '@/components/ContextPanel';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/Modal';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useCostCenters, useStockLocations } from '@/lib/queries';
import { AdjustDialog, EntryDialog, IssueDialog, TransferDialog } from './MovementActions';
import { DateInput } from '@/components/DateInput';

const TYPE_TONE: Record<string, BadgeKind> = {
  ENTRADA: 'ok',
  SAIDA: 'info',
  TRANSFERENCIA: 'mute',
  AJUSTE: 'warn',
};

/**
 * Movimentações de estoque — histórico append-only.
 *
 * Nenhuma linha aqui é editável ou apagável: erro de lançamento se corrige com
 * um ajuste justificado, que também fica registrado. É a mesma regra do
 * Financeiro, aplicada ao físico.
 */
export function MovimentacoesPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const locations = useStockLocations();
  const costCenters = useCostCenters();

  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [locationId, setLocationId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [origin, setOrigin] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<StockMovement | null>(null);
  const [dialog, setDialog] = useState<'entry' | 'issue' | 'transfer' | 'adjust' | null>(null);
  const [reversing, setReversing] = useState(false);

  const listQuery = useQuery({
    queryKey: ['stock-movements', { search, type, origin, locationId, costCenterId, from, to, page }],
    queryFn: () =>
      api.get<Paginated<StockMovement>>('/stock-movements', {
        search: search || undefined,
        type: type || undefined,
        origin: origin || undefined,
        locationId: locationId || undefined,
        costCenterId: costCenterId || undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        perPage: 25,
      }),
  });


  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-balances'] });
    void queryClient.invalidateQueries({ queryKey: ['items'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-locations'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-counts'] });
  };

  const reverseMutation = useMutation({
    mutationFn: (payload: { id: string; reason: string }) =>
      api.post<StockMovement>(`/stock-movements/${payload.id}/reverse`, { reason: payload.reason }),
    onSuccess: () => {
      setReversing(false);
      setSelected(null);
      refresh();
    },
  });

  const columns: Array<Column<StockMovement>> = [
    {
      key: 'movedAt',
      label: 'Quando',
      mono: true,
      render: (row) => formatDateTime(row.movedAt),
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (row) => (
        <StatusBadge kind={TYPE_TONE[row.type] ?? 'mute'}>
          {STOCK_MOVEMENT_TYPE_LABELS[row.type]}
        </StatusBadge>
      ),
    },
    { key: 'sku', label: 'Código', mono: true, render: (row) => row.sku },
    { key: 'itemName', label: 'Item', strong: true, render: (row) => row.itemName },
    { key: 'locationName', label: 'Local', render: (row) => row.locationName },
    { key: 'costCenterName', label: 'Centro de custo', render: (row) => blank(row.costCenterName) },
    {
      key: 'quantityMilli',
      label: 'Quantidade',
      right: true,
      mono: true,
      render: (row) => {
        const negative = row.type === 'SAIDA' || row.quantityMilli < 0;
        return (
          <span style={{ color: negative ? T.red : T.green, fontWeight: 600 }}>
            {negative && row.quantityMilli > 0 ? '−' : ''}
            {formatQty(Math.abs(row.quantityMilli))} {row.unit}
          </span>
        );
      },
    },
    {
      key: 'totalCostCents',
      label: 'Valor',
      right: true,
      mono: true,
      render: (row) => formatCents(row.totalCostCents),
    },
  ];

  const hasFilters = Boolean(search || type || origin || locationId || costCenterId || from || to);

  return (
    <>
      <WorkspaceHeader
        title="Movimentações"
        actions={
          <>
            {can('movimentacoes:criar') && (
              <>
                <Button icon={ArrowDownToLine} onClick={() => setDialog('entry')}>
                  Entrada
                </Button>
                <Button icon={ArrowUpFromLine} onClick={() => setDialog('issue')}>
                  Saída para consumo
                </Button>
                <Button icon={Shuffle} onClick={() => setDialog('transfer')}>
                  Transferir
                </Button>
              </>
            )}
            {can('movimentacoes:aprovar') && (
              <Button variant="primary" icon={Scale} onClick={() => setDialog('adjust')}>
                Ajustar saldo
              </Button>
            )}
          </>
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por item, código ou justificativa…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'type',
              label: 'Tipo',
              value: type,
              // TRANSFERENCIA não é um tipo gravado (a transferência vira
              // SAIDA+ENTRADA com origem TRANSFERENCIA) — filtrá-la aqui não
              // retornaria nada, então ela sai da lista e vira filtro de origem.
              options: STOCK_MOVEMENT_TYPES.filter((value) => value !== 'TRANSFERENCIA').map(
                (value) => ({ value, label: STOCK_MOVEMENT_TYPE_LABELS[value] }),
              ),
              onChange: (value) => {
                setType(value);
                setPage(1);
              },
            },
            {
              key: 'origin',
              label: 'Origem',
              value: origin,
              options: STOCK_MOVEMENT_ORIGINS.map((value) => ({
                value,
                label: STOCK_MOVEMENT_ORIGIN_LABELS[value],
              })),
              onChange: (value) => {
                setOrigin(value);
                setPage(1);
              },
            },
            {
              key: 'location',
              label: 'Local',
              value: locationId,
              options: (locations.data ?? []).map((location) => ({
                value: location.id,
                label: location.name,
              })),
              onChange: (value) => {
                setLocationId(value);
                setPage(1);
              },
            },
            {
              key: 'costCenter',
              label: 'Centro de custo',
              value: costCenterId,
              options: (costCenters.data ?? []).map((center) => ({
                value: center.id,
                label: `${center.code} · ${center.name}`,
              })),
              onChange: (value) => {
                setCostCenterId(value);
                setPage(1);
              },
            },
          ]}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
            <span className="muted">De</span>
            <DateInput
              className="input"
              aria-label="Movimentos a partir de"
              value={from}
              onChange={(iso) => {
                setFrom(iso);
                setPage(1);
              }}
              style={{ width: 140 }}
            />
            <span className="muted">até</span>
            <DateInput
              className="input"
              aria-label="Movimentos até"
              value={to}
              onChange={(iso) => {
                setTo(iso);
                setPage(1);
              }}
              style={{ width: 140 }}
            />
          </label>
        </FilterBar>

        {listQuery.isPending && <TableSkeleton columns={8} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Movimentações de estoque"
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selected?.id ?? null}
            onRowClick={setSelected}
            onPageChange={setPage}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setType('');
                    setOrigin('');
                    setLocationId('');
                    setCostCenterId('');
                    setFrom('');
                    setTo('');
                  }}
                />
              ) : (
                <EmptyState
                  title="Nenhuma movimentação registrada"
                  description="A primeira entrada pode vir de um recebimento de compra, de uma NF-e ou de um lançamento manual."
                />
              )
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.sku} · ${selected.itemName}` : ''}
        subtitle={selected ? formatDateTime(selected.movedAt) : undefined}
        footer={
          selected &&
          !selected.isReversal &&
          selected.origin !== 'TRANSFERENCIA' &&
          can('movimentacoes:aprovar') ? (
            <Button small variant="danger" icon={Undo2} onClick={() => setReversing(true)}>
              Estornar movimentação
            </Button>
          ) : undefined
        }
      >
        {selected && (
          <>
            <ReadField
              label="Tipo"
              value={
                <StatusBadge kind={TYPE_TONE[selected.type] ?? 'mute'}>
                  {STOCK_MOVEMENT_TYPE_LABELS[selected.type]}
                </StatusBadge>
              }
            />
            <ReadField label="Origem" value={STOCK_MOVEMENT_ORIGIN_LABELS[selected.origin]} />
            <ReadField label="Local" value={selected.locationName} />
            <ReadField label="Centro de custo" value={blank(selected.costCenterName)} />
            <ReadField
              label="Quantidade"
              value={`${formatQty(selected.quantityMilli)} ${selected.unit}`}
              mono
            />
            <ReadField label="Custo unitário" value={formatCents(selected.unitCostCents)} mono />
            <ReadField label="Valor total" value={formatCents(selected.totalCostCents)} mono />
            <ReadField
              label="Saldo antes → depois"
              value={`${formatQty(selected.balanceBeforeMilli)} → ${formatQty(selected.balanceAfterMilli)}`}
              mono
            />
            {selected.reason && <ReadField label="Justificativa" value={selected.reason} />}
            {selected.pairId && (
              <p className="muted" style={{ fontSize: 12.5, marginTop: 12 }}>
                Esta linha faz par com a outra ponta da transferência: saída e
                entrada são registradas juntas, nunca uma sem a outra.
              </p>
            )}
          </>
        )}
      </ContextPanel>

      <ConfirmDialog
        open={reversing}
        destructive
        requireReason
        loading={reverseMutation.isPending}
        title="Estornar esta movimentação?"
        confirmLabel="Estornar"
        message={
          <>
            Nada é apagado: entra um contra-movimento na direção oposta, e as duas
            linhas ficam no livro de estoque.
            {reverseMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(reverseMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setReversing(false);
          reverseMutation.reset();
        }}
        onConfirm={(reason) =>
          selected && reverseMutation.mutate({ id: selected.id, reason: reason ?? '' })
        }
      />

      <EntryDialog open={dialog === 'entry'} onClose={() => setDialog(null)} onDone={refresh} />
      <IssueDialog open={dialog === 'issue'} onClose={() => setDialog(null)} onDone={refresh} />
      <TransferDialog open={dialog === 'transfer'} onClose={() => setDialog(null)} onDone={refresh} />
      <AdjustDialog open={dialog === 'adjust'} onClose={() => setDialog(null)} onDone={refresh} />
    </>
  );
}
